import { inject, Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { ChecklistTemplateDto } from 'src/app/models/checklist-process-microservice/checklist-metadata-dto';
import { ClientDto } from 'src/app/models/client/client-dto';
import {
  ClientResponsibleDto,
  ResponsibleType,
  UserResponsibleFilter
} from 'src/app/models/client/client-responsible-dto';
import { ProjectTypeDto } from 'src/app/models/client/project-type-dto';
import { FilterMetadataIds } from 'src/app/models/local/filter-metadata-ids';
import { FilterMetadataValues } from 'src/app/models/local/filter-metadata-values';
import { OrganizationDto } from 'src/app/models/organization.dto';
import { NodeConfigurationType } from 'src/app/models/process/node-configuration-type';
import { UserOrganizationResponsibleDto } from 'src/app/models/user/user-organization-responsible-dto';
import { WorkResponsibleDto } from 'src/app/models/work/work-responsible/work-responsible-dto';
import { AuthService } from '../../api/auth-api';
import {
  ClientService,
  ConfigurationElementsService,
  OrganizationResponsibleService,
  ProjectTypesService
} from '../../api/client-api';
import {
  ChecklistService,
  ChecklistTemplateMicroService,
  ProcessElementsService,
  WorkResponsibleService
} from '../../api/process-api';
import { OrganizationMicroservice } from '../../api/user-api/organization.micro.service';
import { CloneService } from '../clone.service';

@Injectable({
  providedIn: 'root'
})
export class FilterMetadataService {
  private readonly projectTypeService = inject(ProjectTypesService);
  private readonly processElementsService = inject(ProcessElementsService);
  private readonly clientService = inject(ClientService);
  private readonly organizationService = inject(OrganizationMicroservice);
  private readonly workResponsibleService = inject(WorkResponsibleService);
  private readonly cloneService = inject(CloneService);
  private readonly authService = inject(AuthService);
  private readonly configurationElementsService = inject(
    ConfigurationElementsService
  );
  private readonly checklistService = inject(ChecklistService);
  private readonly checklistTemplateService = inject(
    ChecklistTemplateMicroService
  );
  private readonly organizationResponsibleService = inject(
    OrganizationResponsibleService
  );

  getFilterMetadata$(
    filterMetadataIds: FilterMetadataIds
  ): Observable<FilterMetadataValues> {
    if (!filterMetadataIds) {
      return;
    }

    const ids = this.cloneService.deepClone(filterMetadataIds);

    const currentUser = new ClientResponsibleDto();
    currentUser.userId = this.authService.getUserId();
    currentUser.name = this.authService.getUserFullName();

    const requests: Observable<any>[] = [];

    // process section
    requests.push(
      ids.configurationElementProcessNodeIds?.length > 0
        ? this.processElementsService.getProcessElementByIds$(
            ids.configurationElementProcessNodeIds,
            NodeConfigurationType.All
          )
        : of(null)
    );

    requests.push(
      ids.configurationElementIds?.length > 0
        ? this.configurationElementsService.getElementsName$(
            ids.configurationElementIds
          )
        : of(null)
    );

    // responsible section
    const filter: UserResponsibleFilter[] = this.createResponsibleFilter(ids);
    requests.push(
      filter.length > 0
        ? this.clientService.getUsersAsClientResponsibles$(filter)
        : of(null)
    );

    requests.push(
      ids.organizationResponsibleIds?.length > 0
        ? this.organizationResponsibleService.getOrganizationResponsibles$(
            null,
            null,
            null,
            null,
            false,
            null,
            ids.organizationResponsibleIds
          )
        : of(null)
    );

    // client section
    requests.push(
      ids.clientIds?.length > 0
        ? this.clientService.getBaseClients$(ids.clientIds)
        : of(null)
    );

    requests.push(
      ids.organizationIds?.length > 0
        ? this.organizationService.getOrganizationsByIds$(
            ids.organizationIds,
            false
          )
        : of(null)
    );

    requests.push(
      ids.groups?.length > 0
        ? this.clientService.getGroups$(ids.groups)
        : of(null)
    );

    requests.push(
      ids.marketSegments?.length > 0
        ? this.clientService.getMarketSegments$(ids.marketSegments)
        : of(null)
    );

    requests.push(
      ids.projectTypes?.length > 0
        ? this.projectTypeService.getProjectTypesByIds$(ids.projectTypes)
        : of(null)
    );

    //work section
    requests.push(
      ids.teamResponsibleIds?.length > 0
        ? this.organizationService.getOrganizationsByIds$(
            ids.teamResponsibleIds,
            false
          )
        : of(null)
    );

    requests.push(
      ids.workResponsibleCompositeIds?.length > 0
        ? this.workResponsibleService.getWorkResponsibles$(
            null,
            null,
            true,
            true,
            true,
            false,
            ids.workResponsibleCompositeIds
          )
        : of(null)
    );

    //legacy checklists
    requests.push(
      ids.checklistTemplateIds?.length > 0
        ? this.checklistService.getChecklists$({
            checklistIds: ids.checklistTemplateIds
          })
        : of(null)
    );

    //new checklists
    requests.push(
      ids.checklistTemplateIds?.length > 0
        ? this.checklistTemplateService.getChecklists$({
            checklistIds: ids.checklistTemplateIds
          })
        : of(null)
    );

    const requests$ = forkJoin(requests).pipe(
      map(
        ([
          configurationElementProcessNode,
          configurationElement,
          responsibles,
          organizationResponsibles,
          clients,
          organizations,
          groups,
          marketSegments,
          projectTypes,
          teamResponsibles,
          workResponsibles,
          checklistTemplatesLegacy,
          checklistTemplatesNew
        ]) => {
          const result = {
            configurationElementProcessNode: configurationElementProcessNode, // as ProcessElementDto[], // TODO: see comment from FilterMetadataValues
            configurationElement: configurationElement, // TODO: see comment from FilterMetadataValues

            allResponsibles: (responsibles as ClientResponsibleDto[])?.filter(
              r => r.type == null
            ),
            customerResponsibles: (
              responsibles as ClientResponsibleDto[]
            )?.filter(r => r.type == ResponsibleType.CustomerResponsible),
            accountingResponsibles: (
              responsibles as ClientResponsibleDto[]
            )?.filter(r => r.type == ResponsibleType.AccountingResponsible),
            payResponsibles: (responsibles as ClientResponsibleDto[])?.filter(
              r => r.type == ResponsibleType.PayrollResponsible
            ),
            yearEndResponsibles: (
              responsibles as ClientResponsibleDto[]
            )?.filter(r => r.type == ResponsibleType.YearEndResponsible),
            charteredAccountantResponsibles: (
              responsibles as ClientResponsibleDto[]
            )?.filter(
              r => r.type == ResponsibleType.CharteredAccountantResponsible
            ),
            accountingBackupResponsibles: (
              responsibles as ClientResponsibleDto[]
            )?.filter(
              r => r.type == ResponsibleType.AccountingBackupResponsible
            ),
            accountingOnboardingResponsibles: (
              responsibles as ClientResponsibleDto[]
            )?.filter(
              r => r.type == ResponsibleType.AccountingOnboardingResponsible
            ),
            payrollBackupResponsibles: (
              responsibles as ClientResponsibleDto[]
            )?.filter(r => r.type == ResponsibleType.PayrollBackupResponsible),
            payrollOnboardingResponsibles: (
              responsibles as ClientResponsibleDto[]
            )?.filter(
              r => r.type == ResponsibleType.PayrollOnboardingResponsible
            ),

            organizationResponsibles:
              (organizationResponsibles as UserOrganizationResponsibleDto[]) !=
              null
                ? (
                    organizationResponsibles as UserOrganizationResponsibleDto[]
                  ).map(
                    u =>
                      <ClientResponsibleDto>{
                        userId: u.id,
                        name: u.name
                      }
                  )
                : [],

            clientDtos: clients as ClientDto[],
            organizationsDtos: organizations as OrganizationDto[],
            groups: groups as string[],
            marketSegments: marketSegments as string[],
            projectTypes: projectTypes as ProjectTypeDto[],

            teamResponsibles: teamResponsibles as OrganizationDto[],
            workResponsibles: workResponsibles as WorkResponsibleDto[],

            checklistTemplatesLegacy:
              checklistTemplatesLegacy as ChecklistTemplateDto[],
            checklistTemplatesNew:
              checklistTemplatesNew as ChecklistTemplateDto[]
          };

          if (ids.isCurrentUserAllResponsible) {
            result.allResponsibles = this.addCurrentUser(
              result.allResponsibles,
              currentUser
            );
          }
          if (ids.isCurrentUserCustomerResponsible) {
            result.customerResponsibles = this.addCurrentUser(
              result.customerResponsibles,
              currentUser
            );
          }
          if (ids.isCurrentUserAccountingResponsible) {
            result.accountingResponsibles = this.addCurrentUser(
              result.accountingResponsibles,
              currentUser
            );
          }
          if (ids.isCurrentUserPayResponsible) {
            result.payResponsibles = this.addCurrentUser(
              result.payResponsibles,
              currentUser
            );
          }
          if (ids.isCurrentUserYearEndResponsible) {
            result.yearEndResponsibles = this.addCurrentUser(
              result.yearEndResponsibles,
              currentUser
            );
          }
          if (ids.isCurrentUserCharteredAccountantResponsible) {
            result.charteredAccountantResponsibles = this.addCurrentUser(
              result.charteredAccountantResponsibles,
              currentUser
            );
          }
          if (ids.isCurrentUserAccountingBackupResponsible) {
            result.yearEndResponsibles = this.addCurrentUser(
              result.accountingBackupResponsibles,
              currentUser
            );
          }
          if (ids.isCurrentUserAccountingOnboardingResponsible) {
            result.yearEndResponsibles = this.addCurrentUser(
              result.accountingOnboardingResponsibles,
              currentUser
            );
          }
          if (ids.isCurrentUserPayrollBackupResponsible) {
            result.payrollBackupResponsibles = this.addCurrentUser(
              result.payrollBackupResponsibles,
              currentUser
            );
          }
          if (ids.isCurrentUserPayrollOnboardingResponsible) {
            result.payrollOnboardingResponsibles = this.addCurrentUser(
              result.payrollOnboardingResponsibles,
              currentUser
            );
          }
          if (ids.isCurrentUserOrganizationResponsible) {
            result.organizationResponsibles = this.addCurrentUser(
              result.organizationResponsibles,
              currentUser
            );
          }

          return result;
        }
      )
    );

    return requests$;
  }

  private addCurrentUser(
    responsibles: ClientResponsibleDto[],
    currentUser: ClientResponsibleDto
  ): ClientResponsibleDto[] {
    responsibles ??= [];
    responsibles = [currentUser, ...responsibles];
    return responsibles;
  }

  private createResponsibleFilter(
    filterMetadataIds: FilterMetadataIds
  ): UserResponsibleFilter[] {
    const responsiblesFilter: UserResponsibleFilter[] = [];

    filterMetadataIds.allResponsibleIds?.forEach(element => {
      responsiblesFilter.push({ userId: element, type: null });
    });

    filterMetadataIds.customerResponsibleIds?.forEach(element => {
      responsiblesFilter.push({
        userId: element,
        type: ResponsibleType.CustomerResponsible
      });
    });

    filterMetadataIds.accountingResponsibleIds?.forEach(element => {
      responsiblesFilter.push({
        userId: element,
        type: ResponsibleType.AccountingResponsible
      });
    });

    filterMetadataIds.payResponsibleIds?.forEach(element => {
      responsiblesFilter.push({
        userId: element,
        type: ResponsibleType.PayrollResponsible
      });
    });

    filterMetadataIds.yearEndResponsibleIds?.forEach(element => {
      responsiblesFilter.push({
        userId: element,
        type: ResponsibleType.YearEndResponsible
      });
    });

    filterMetadataIds.charteredAccountantResponsibleIds?.forEach(element => {
      responsiblesFilter.push({
        userId: element,
        type: ResponsibleType.CharteredAccountantResponsible
      });
    });

    filterMetadataIds.accountingBackupResponsibleIds?.forEach(element => {
      responsiblesFilter.push({
        userId: element,
        type: ResponsibleType.AccountingBackupResponsible
      });
    });

    filterMetadataIds.accountingOnboardingResponsibleIds?.forEach(element => {
      responsiblesFilter.push({
        userId: element,
        type: ResponsibleType.AccountingOnboardingResponsible
      });
    });

    filterMetadataIds.payrollBackupResponsibleIds?.forEach(element => {
      responsiblesFilter.push({
        userId: element,
        type: ResponsibleType.PayrollBackupResponsible
      });
    });

    filterMetadataIds.payrollOnboardingResponsibleIds?.forEach(element => {
      responsiblesFilter.push({
        userId: element,
        type: ResponsibleType.PayrollOnboardingResponsible
      });
    });

    return responsiblesFilter;
  }
}
